// js/shared/public-site.js — the homepage's proof and testimonials, read from
// the database rather than typed into the page. QUEUE ITEM GV.
//
// Two public views, both with the publishable key and both one-way doors
// (migration 20261023000000):
//
//   public.site_profile         — the Google reviews address, the "clients
//                                 served" number with its "as of" date and
//                                 (queue item KV) the five Safety and trust
//                                 facts, from Settings › Business.
//   public.testimonials_public  — published quotes whose client has a signed
//                                 media release on file and has not revoked it.
//
// PROOF THAT IS A RECORD, OR NOTHING. The Google row is RENDERED FROM THE
// ADDRESS JK ENTERED — a link to his reviews, never a star count somebody typed
// (NN/g trustworthy design: link to outside reviews rather than self-hosted
// claims). The clients-served number shows only with its "as of" date, because
// a count without a date is a claim that ages silently. A quote shows only
// because a row with a consent document says it may. With no network, no rows
// or nothing set, every one of these stays hidden and the page is exactly its
// markup — the three statements in the trainer's own voice and the three facts
// that are true on paper.
//
// PURE HALF / DOM HALF. `proofFacts` and `testimonialCardsHtml` take plain data
// and return plain data or a string, so tests/unit/shared/gv-public-site.test.mjs
// runs them in node. The painter below them is the only part that touches the
// document.

import { marketingPack } from './marketing-i18n.js';
import { safeUrl } from './card-rows.js';

// The publishable key, as index.html, intake.html and js/shared/card-page.js
// write it. It reaches the two views above and nothing else.
const SUPABASE_URL = 'https://rvrvfdjmhwymkbmrzoza.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_fs3ThPGEwQujZTcUVux8Tw_dLe3Bknl';

const escHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * The strip's optional facts from one `site_profile` row.
 * → { google: 'https://…' | null, served: { n: 40, asOf: '2026-09-01' } | null }
 */
export function proofFacts(row) {
  const r = row || {};
  const google = safeUrl(r.google_review_url) || null;
  const n = Number(String(r.clients_served == null ? '' : r.clients_served).trim());
  const asOf = String(r.clients_served_as_of || '').trim();
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(asOf) && !Number.isNaN(Date.parse(asOf + 'T12:00:00Z'));
  const served = (Number.isInteger(n) && n > 0 && dateOk) ? { n, asOf } : null;
  return { google, served };
}

/** "as of September 2026" in the page's language. */
export function asOfWords(iso, lang) {
  const pack = marketingPack(lang);
  const d = new Date(String(iso) + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return String(pack['proof.asOf'] || 'as of {date}').replace('{date}', month);
}

/**
 * Up to three published quotes as `.tcard` markup. A row missing its quote or
 * first name is skipped rather than drawn half-empty. Attribution is first name
 * and last initial, then the situation — never a surname, never a photo.
 */
export function testimonialCardsHtml(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r && String(r.quote || '').trim() && String(r.first_name || '').trim())
    .slice(0, 3)
    .map((r) => {
      const initial = String(r.last_initial || '').trim().slice(0, 1);
      const name = String(r.first_name).trim() + (initial ? ' ' + initial.toUpperCase() + '.' : '');
      const situation = String(r.situation || '').trim();
      return '<figure class="tcard" data-consent="on-file">'
        + '<blockquote class="tcard-quote">' + escHtml(String(r.quote).trim()) + '</blockquote>'
        + '<figcaption class="tcard-attribution"><span class="tcard-name">' + escHtml(name) + '</span>'
        + (situation ? '<span class="tcard-type">' + escHtml(situation) + '</span>' : '')
        + '</figcaption></figure>';
    })
    .join('');
}

export const SAFETY_ROWS = Object.freeze(['cpr', 'insurance', 'background', 'ages', 'watch']);

/**
 * Queue item KV (decision d26 a) — the Safety and trust facts from one
 * `site_profile` row, in a language. → { cpr: 'Current, …' | null, … }. A
 * Spanish row with no Spanish fact falls back to the English one; a row with
 * neither is null and is not drawn. Never a fact the row does not hold.
 */
export function safetyFacts(row, lang) {
  const r = row || {};
  const es = String(lang || '').startsWith('es');
  const out = {};
  for (const k of SAFETY_ROWS) {
    const en = String(r['safety_' + k + '_en'] || '').trim();
    const sp = String(r['safety_' + k + '_es'] || '').trim();
    out[k] = (es && sp) || en || null;
  }
  return out;
}

// ── the DOM half ─────────────────────────────────────────────────────────

let facts = { google: null, served: null };
let profileRow = null;

function pageLang() {
  return (document.documentElement.getAttribute('lang') || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
}

function paint() {
  const lang = pageLang();
  const served = document.querySelector('[data-proof="served"]');
  if (served) {
    served.hidden = !facts.served;
    if (facts.served) {
      served.querySelector('[data-proof-served]').textContent = facts.served.n.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US');
      served.querySelector('[data-proof-asof]').textContent = asOfWords(facts.served.asOf, lang);
    }
  }
  const google = document.querySelector('[data-proof="google"]');
  if (google) {
    google.hidden = !facts.google;
    if (facts.google) google.querySelector('[data-proof-google]').setAttribute('href', facts.google);
  }
  const list = document.querySelector('[data-safety]');
  if (list) {
    const safety = safetyFacts(profileRow, lang);
    let any = false;
    for (const k of SAFETY_ROWS) {
      const li = list.querySelector('[data-safety-row="' + k + '"]');
      if (!li) continue;
      li.hidden = !safety[k];
      if (safety[k]) { any = true; li.querySelector('[data-safety-fact]').textContent = safety[k]; }
    }
    list.hidden = !any;
  }
}

async function read(path) {
  const res = await fetch(SUPABASE_URL + '/rest/v1/' + path,
    { headers: { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' } });
  if (!res.ok) return [];
  const rows = await res.json();
  return Array.isArray(rows) ? rows : [];
}

/**
 * Queue items BS 7, CY and GV — a card renders on consent, not on markup. Only
 * this module writes `.tcard`s, from public.testimonials_public (published,
 * with a signed release, not revoked); this is the last line: a card that is
 * not `data-consent="on-file"` comes off the page whoever put it there.
 */
export function gateConsent(root = document) {
  root.querySelectorAll('#testimonials .tcard:not([data-consent="on-file"])').forEach((card) => card.remove());
}

async function boot() {
  gateConsent();
  try {
    const [profile, quotes] = await Promise.all([
      read('site_profile?select=google_review_url,clients_served,clients_served_as_of,'
        + SAFETY_ROWS.map((k) => 'safety_' + k + '_en,safety_' + k + '_es').join(',') + '&limit=1'),
      read('testimonials_public?select=quote,first_name,last_initial,situation&order=published_at.desc&limit=3'),
    ]);
    profileRow = profile[0] || null;
    facts = proofFacts(profileRow);
    paint();
    const html = testimonialCardsHtml(quotes);
    const grid = document.querySelector('#testimonials .testimonials-grid');
    if (html && grid) {
      grid.innerHTML = html;
      const own = document.querySelector('#testimonials .stories-list');
      if (own) own.remove();
      // Queue item KV — over real quotes the head is theirs, not "What
      // families ask me first" (which heads the trainer's own three lines).
      const label = document.querySelector('#testimonials [data-i18n="stories.label"]');
      const h2 = document.querySelector('#testimonials [data-i18n="stories.h2"]');
      if (label) label.setAttribute('data-i18n', 'stories.quotesLabel');
      if (h2) h2.setAttribute('data-i18n', 'stories.quotesH2');
      const pack = marketingPack(pageLang());
      if (label) label.textContent = pack['stories.quotesLabel'];
      if (h2) h2.textContent = pack['stories.quotesH2'];
    }
    gateConsent();
  } catch (_) {
    // Offline, blocked, or a view unreachable: the page already shows the state
    // this would fall back to, which is the markup.
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.kafPaintPublicSite = paint;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
